import { defineComponent as _defineComponent } from 'vue'
import type { NoSearchFilterError } from '@@/bits/content_picker'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { extractUrl } from '@@/bits/html_parser'
import { isUrl } from '@@/bits/url'
import OzBackButton from '@@/library/v4/components/OzBackButton.vue'
import OzCloseButton from '@@/library/v4/components/OzCloseButton.vue'
import OzIconButton, { OzIconButtonColorScheme } from '@@/library/v4/components/OzIconButton.vue'
import OzPlainButton, { OzPlainButtonColorScheme } from '@@/library/v4/components/OzPlainButton.vue'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'ContentPickerSearchBar',
  props: {
    xBackButton: { type: Boolean },
    xClipboardButton: { type: Boolean },
    shouldWiggle: { type: Boolean },
    shouldForceFocusOnMount: { type: Boolean },
    searchBarPlaceholder: null,
    searchQuery: null,
    submitButtonDataId: null,
    canAcceptLink: { type: Boolean }
  },
  emits: ["back", "blur", "search", "clear-search", "clear-search-error", "click", "close", "copy", "error", "focus", "input", "paste-click", "select"],
  setup(__props: any, { expose, emit }: { emit: ({
  (name: 'back'): void
  (name: 'blur'): void
  (name: 'search', query: string): void
  (name: 'clear-search'): void
  (name: 'clear-search-error'): void
  (name: 'click'): void
  (name: 'close'): void
  (name: 'copy'): void
  (name: 'error', error: NoSearchFilterError): void
  (name: 'focus'): void
  (name: 'input', value: string): void
  (name: 'paste-click'): void
  (name: 'select', data: string): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  xBackButton: boolean
  xClipboardButton: boolean
  shouldWiggle: boolean
  shouldForceFocusOnMount: boolean
  searchBarPlaceholder: string
  searchQuery: string
  submitButtonDataId?: string
  canAcceptLink?: boolean
};





// https://docs.microsoft.com/en-us/rest/api/cognitiveservices-bingsearch/bing-images-api-v5-reference
// The maximum URL length is 2,048 characters. To ensure that your URL length does not exceed the limit,
// the maximum length of your query parameters should be less than 1,500 characters.
// If the URL exceeds 2,048 characters, the server returns 404 Not found.
// Need to account for percent-encoded multi-code-point UTF-8 string.
const MAX_SEARCH_LENGTH = 1000

const { isPhone } = storeToRefs(useWindowSizeStore())

const searchBar = ref<HTMLInputElement | null>(null)

const isSearchBarFocused = ref<boolean>(false)

const shouldShowClipboardButton = computed(
  () => props.xClipboardButton && !isPhone.value && !device.safari && !props.searchQuery,
)
const shouldShowSubmitButton = computed(() =>
  isSearchBarFocused.value ? !shouldShowClipboardButton.value : props.searchQuery,
)
const isShowingRightButton = computed(() => shouldShowClipboardButton.value || shouldShowSubmitButton.value)

const handleCopyText = (): void => {
  emit('copy')
}

const handleSearchBarFocus = (): void => {
  isSearchBarFocused.value = true
  emit('focus')
}

const onInput = (event): void => {
  emit('input', event.target.value)
  emit('clear-search-error')
}

const select = (data: string): void => {
  emit('select', data)
}

const unfocusForTouchDevices = () => {
  if (device.touchable && searchBar.value) {
    // If it's touchable, keyboard will popup, we don't want that
    searchBar.value.blur()
  }
}

// If the search query is a link, use it as post attachment by calling this.select
// Else use it to search with this.search
// If we are in link input mode, default to web search
const processQuery = (): void => {
  const query = props.searchQuery
  emit('clear-search-error')
  unfocusForTouchDevices()

  if (!query) {
    emit('clear-search')
    return
  }
  if (props.canAcceptLink) {
    if (isUrl(query)) {
      select(query)
      return
    }
    if (isUrl(`https://${query}`)) {
      select(`https://${query}`)
      return
    }
    const extractedUrl = extractUrl(query)
    if (extractedUrl) {
      select(extractedUrl)
      return
    }
  }

  emit('search', query)
}

const handleSearchBarFocusOut = (): void => {
  isSearchBarFocused.value = false
  if (device.iphone) {
    processQuery()
  }
  emit('blur')
}

const resetSearch = (): void => {
  emit('clear-search')
  searchBar.value?.focus()
}

const focusSearchBar = (): void => {
  searchBar.value?.focus()
}

const handleEnter = (event): void => {
  if (!props.searchQuery) {
    event.preventDefault()
  }
}

onMounted((): void => {
  if ((!device.touchable || props.shouldForceFocusOnMount) && searchBar.value) {
    // If it's touchable, keyboard will popup, we don't want that
    searchBar.value.focus()
  }
})

expose({
  focusSearchBar,
})

return { __sfc: true,props, emit, MAX_SEARCH_LENGTH, isPhone, searchBar, isSearchBarFocused, shouldShowClipboardButton, shouldShowSubmitButton, isShowingRightButton, handleCopyText, handleSearchBarFocus, onInput, select, unfocusForTouchDevices, processQuery, handleSearchBarFocusOut, resetSearch, focusSearchBar, handleEnter, dir, OzBackButton, OzCloseButton, OzIconButton, OzIconButtonColorScheme, OzPlainButton, OzPlainButtonColorScheme }
}

})